/**
 * Labels for the vanilla widget bundles (T-245).
 *
 * ── How strings get in here ─────────────────────────────────────────────────
 *
 * The community app ships 24 locales; these bundles shipped one, so a member
 * reading in Portuguese opened the desktop cart and got "Checkout", "Total",
 * "Your cart is empty". There is no i18n runtime in a vanilla bundle and no
 * sane way to bake two dozen locales into one, so the app resolves the strings
 * server-side -- it already knows the reader's locale -- and hands them over
 * finished on `window.__COBUNTU_WIDGET_I18N__`, namespaced per widget.
 *
 * This is the same mechanism `__COBUNTU_BUILD_LINE__` already used, for the
 * same reason, and it keeps the bundles byte-stable: no string baked in means
 * no rebuild when a translation changes.
 *
 * ── The fallback is the contract, not a safety net ──────────────────────────
 *
 * Every call passes an English fallback and that is load-bearing. On a
 * CUSTOMER'S own site these widgets are embedded with no next-intl behind
 * them, so the global is absent and the fallback IS the string there. A
 * missing global has to mean English, never a blank row or an empty button.
 */

type Dict = Record<string, unknown>;

/**
 * A lookup bound to one widget's namespace.
 *
 * Reads the global on every call rather than caching it: the widgets build
 * their markup lazily on first open, but `beforeInteractive` ordering is a
 * promise about script execution, not about when a drawer is opened, and a
 * cached empty dict would outlive the fix.
 */
export function makeT(namespace: string): (key: string, fallback: string) => string {
  return (key, fallback) => {
    const root = (window as unknown as { __COBUNTU_WIDGET_I18N__?: Dict }).__COBUNTU_WIDGET_I18N__;
    const ns = root && typeof root === "object" ? (root[namespace] as Dict | undefined) : undefined;
    const v = ns && typeof ns === "object" ? ns[key] : undefined;
    return typeof v === "string" && v.length > 0 ? v : fallback;
  };
}

/**
 * Escape a translated string before interpolating it into markup.
 *
 * These come from message files rather than from users, but they are the one
 * thing in these bundles built from data that is not in the file, and every
 * widget assembles its DOM with template strings.
 */
export function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
